import React, { useEffect, useState } from "react";
import {
  Check,
  CheckCircle,
  EllipsisVerticalIcon,
  Eye,
  FileText,
  X,
  XCircle,
} from "lucide-react";
import { formatDisplayDate } from "../../../../utils/Date";
import type { ActionType, Objection } from "./types";

interface ObjectionRequestsTableProps {
  objections: Objection[];
  onViewDetails: (objection: Objection) => void;
  onAction: (objection: Objection, actionType: ActionType) => void;
}

const getStatusClassName = (status: Objection["status"]) => {
  switch (status) {
    case "approved":
      return "bg-green-100 text-green-700";
    case "rejected":
      return "bg-red-100 text-red-700";
    case "pending":
      return "bg-yellow-100 text-yellow-700";
    default:
      return "bg-gray-100 text-gray-700";
  }
};

const getApproveButtonClassName = (status: Objection["status"]) =>
  status === "approved"
    ? "cursor-not-allowed bg-green-300 text-white opacity-100"
    : "bg-green-600 text-green-100 hover:bg-green-800";

const getRejectButtonClassName = (status: Objection["status"]) =>
  status === "rejected"
    ? "cursor-not-allowed bg-red-300 text-white opacity-100"
    : "bg-red-600 text-red-100 hover:bg-red-800";

export const ObjectionRequestsTable: React.FC<ObjectionRequestsTableProps> = ({
  objections,
  onViewDetails,
  onAction,
}) => {
  const [menuPosition, setMenuPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const [expandedActionRow, setExpandedActionRow] = useState<string | null>(
    null,
  );

  useEffect(() => {
    const handleClickOutside = () => {
      setExpandedActionRow(null);
      setMenuPosition(null);
    };

    if (expandedActionRow) {
      document.addEventListener("click", handleClickOutside);
    }

    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [expandedActionRow]);

  const openMenu = (
    event: React.MouseEvent<HTMLButtonElement>,
    objectionId: string,
  ) => {
    event.stopPropagation();
    const rect = event.currentTarget.getBoundingClientRect();
    setMenuPosition({
      top: rect.bottom + 4,
      left: rect.right - 140,
    });
    setExpandedActionRow(
      expandedActionRow === objectionId ? null : objectionId,
    );
  };

  const selectAction = (objection: Objection, actionType: ActionType) => {
    onAction(objection, actionType);
    setExpandedActionRow(null);
    setMenuPosition(null);
  };

  return (
    <div className="space-y-5">
      <div className="bg-white rounded-xl shadow-sm border border-gray-100">
        <div
          className="overflow-x-auto overflow-y-auto"
          style={{ maxHeight: "600px" }}
        >
          <table className="min-w-full w-full">
            <thead className="bg-gray-50 border border-gray-100">
              <tr>
                <th className="text-left p-2 text-gray-800 font-medium">
                  CNR Number
                </th>
                <th className="text-left p-2 text-gray-800 font-medium">
                  Objection Type
                </th>
                <th className="text-left p-2 text-gray-800 font-medium">
                  Priority Level
                </th>
                <th className="text-left p-2 text-gray-800 font-medium">
                  Status
                </th>
                <th className="text-left p-2 text-gray-800 font-medium">
                  Hearing Date
                </th>
                <th className="text-left p-2 text-gray-800 font-medium">
                  Filed Date
                </th>
                <th className="text-left p-2 text-gray-800 font-medium">
                  User Name
                </th>
                <th className="text-left p-2 text-gray-800 font-medium">
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {objections.length > 0 ? (
                objections.map((objection) => (
                  <tr
                    key={objection?._id}
                    className="hover:bg-gray-50 transition"
                  >
                    <td className="px-4 py-3 text-xs">
                      <div className="font-semibold uppercase text-gray-600">
                        {objection.cnr}
                      </div>
                    </td>
                    <td className="text-left p-2 text-gray-800 font-medium">
                      <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded-md text-xs font-semibold">
                        {objection?.objectionType?.toUpperCase()}
                      </span>
                    </td>
                    <td className="text-left p-2 text-gray-800 font-medium">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold text-gray-600 uppercase">
                        {objection.priorityLevel}
                      </span>
                    </td>
                    <td className="text-left p-2 text-gray-800 font-medium">
                      <span
                        className={`inline-flex pill items-center px-3 py-1 rounded-full text-xs font-semibold uppercase ${getStatusClassName(
                          objection.status,
                        )}`}
                      >
                        {objection.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-700">
                      {formatDisplayDate(objection.nextHearingDate)}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-700">
                      {formatDisplayDate(objection.createdAt)}
                    </td>
                    <td className="px-4 py-3 text-xs font-semibold text-gray-600 uppercase">
                      {objection?.advocateDetails?.name}
                    </td>
                    <td className="py-3">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => onViewDetails(objection)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-md transition-colors duration-150"
                          title="View Details"
                          type="button"
                        >
                          <Eye className="h-3.5 w-3.5" />
                        </button>
                        <button
                          onClick={() => selectAction(objection, "approve")}
                          className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors duration-150 disabled:opacity-100 ${getApproveButtonClassName(
                            objection.status,
                          )}`}
                          title={
                            objection.status === "approved"
                              ? "Approved"
                              : "Approve"
                          }
                          disabled={objection.status === "approved"}
                          type="button"
                        >
                          <Check className="h-3.5 w-3.5" />
                        </button>
                        <button
                          onClick={() => selectAction(objection, "reject")}
                          className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors duration-150 disabled:opacity-100 ${getRejectButtonClassName(
                            objection.status,
                          )}`}
                          title={
                            objection.status === "rejected"
                              ? "Rejected"
                              : "Reject"
                          }
                          disabled={objection.status === "rejected"}
                          type="button"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>

                        {objection.status === "pending" && (
                          <div className="relative hidden">
                            <button
                              onClick={(event) =>
                                openMenu(event, objection._id)
                              }
                              className="inline-flex items-center text-xs font-medium text-gray-700"
                              type="button"
                            >
                              <EllipsisVerticalIcon />
                            </button>
                            {expandedActionRow === objection._id &&
                              menuPosition && (
                                <div
                                  onClick={(event) => event.stopPropagation()}
                                  className="fixed z-[9999] bg-white rounded-lg shadow-xl border border-gray-200 min-w-[140px]"
                                  style={{
                                    top: menuPosition.top,
                                    left: menuPosition.left,
                                  }}
                                >
                                  <button
                                    type="button"
                                    onClick={() =>
                                      selectAction(objection, "approve")
                                    }
                                    className="w-full flex items-center gap-2 px-3 py-2 text-xs font-medium text-green-700 hover:bg-green-50"
                                  >
                                    <CheckCircle className="h-3.5 w-3.5" />
                                    Approve
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() =>
                                      selectAction(objection, "reject")
                                    }
                                    className="w-full flex items-center gap-2 px-3 py-2 text-xs font-medium text-red-700 hover:bg-red-50"
                                  >
                                    <XCircle className="h-3.5 w-3.5" />
                                    Reject
                                  </button>
                                </div>
                              )}
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={8}
                    className="px-6 py-20 text-center text-gray-500"
                  >
                    <div className="flex flex-col items-center justify-center space-y-3">
                      <FileText className="h-12 w-12 text-gray-300" />
                      <h3 className="text-lg font-medium text-gray-600">
                        Objection List Not Found
                      </h3>
                      <p className="text-xs text-gray-400">
                        No objection requests are available.
                      </p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
